import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import {
  getAttendanceSummary,
  getTodayAttendance,
  checkIn,
  checkOut,
  AttendanceSummary,
} from '../services/eagleVision'

interface ResultNotification {
  type: 'success' | 'error'
  title: string
  message: string
}

export function useAttendance() {
  const [summary, setSummary] = useState<AttendanceSummary | null>(null)
  const [summaryError, setSummaryError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [needsAuth, setNeedsAuth] = useState(false)

  const [isCheckingIn, setIsCheckingIn] = useState(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [checkedInTime, setCheckedInTime] = useState<string | null>(null)
  const [checkedOutTime, setCheckedOutTime] = useState<string | null>(null)

  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [locationLoading, setLocationLoading] = useState(true)

  const [result, setResult] = useState<ResultNotification | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Auto-dismiss result after 5s
  useEffect(() => {
    if (result) {
      const timer = setTimeout(() => setResult(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [result])

  const [locationError, setLocationError] = useState<string | null>(null)

  // Get GPS location with watchPosition for continuous updates
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationLoading(false)
      setLocationError('Geolocation is not supported by this browser')
      return
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setLocation({ lat: position.coords.latitude, lng: position.coords.longitude })
        setLocationLoading(false)
        setLocationError(null)
      },
      (error) => {
        setLocationLoading(false)
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable GPS in your browser settings.')
            break
          case error.POSITION_UNAVAILABLE:
            setLocationError('GPS signal unavailable. Please try moving to an open area.')
            break
          case error.TIMEOUT:
            setLocationError('GPS timed out. Retrying...')
            break
          default:
            setLocationError('Unable to get GPS location')
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    )

    return () => navigator.geolocation.clearWatch(watchId)
  }, [])

  const getErrorMessage = useCallback((err: unknown, fallback: string): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { data?: { needs_auth?: boolean; message?: string } } }
      if (axiosErr.response?.data?.needs_auth) {
        setNeedsAuth(true)
        return 'Please login with Eagle Vision to access attendance features'
      }
      return axiosErr.response?.data?.message || fallback
    }
    return fallback
  }, [])

  const fetchSummary = useCallback(async () => {
    try {
      const data = await getAttendanceSummary()
      setSummary(data)
      setSummaryError(false)
      setNeedsAuth(false)
      // Update check-in/out from EV API (authoritative source)
      if (data.check_in) setCheckedInTime(data.check_in)
      if (data.check_out) setCheckedOutTime(data.check_out)
    } catch (err: unknown) {
      setSummaryError(true)
      const msg = getErrorMessage(err, '')
      if (msg && needsAuth) {
        setError(msg)
      }
    }
  }, [getErrorMessage, needsAuth])

  // Initial load: first restore from local DB, then refresh from EV
  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setError(null)

      // 1. Load from our database first (instant, survives app close)
      try {
        const local = await getTodayAttendance()
        if (local) {
          if (local.check_in_time) setCheckedInTime(local.check_in_time)
          if (local.check_out_time) setCheckedOutTime(local.check_out_time)
        }
      } catch {
        // Ignore - will fall through to EV summary
      }

      // 2. Then refresh from EV summary (authoritative, updates DB too)
      await fetchSummary()

      setIsLoading(false)
    }
    load()
  }, [fetchSummary])

  const handleCheckIn = async (image?: string, locationName?: string) => {
    if (!location) {
      setResult({
        type: 'error',
        title: 'GPS Required',
        message: locationError || 'GPS location is not available. Please enable location services and try again.',
      })
      return
    }
    setIsCheckingIn(true)
    setError(null)
    setResult(null)
    try {
      const res = await checkIn(location.lat, location.lng, locationName || undefined, image || undefined)
      if (res.success) {
        const ciTime = res.check_in_time || new Date().toISOString()
        setCheckedInTime(ciTime)
        setResult({
          type: 'success',
          title: 'Check-In Successful',
          message: res.message || `Checked in at ${res.check_in_time ? format(new Date(res.check_in_time), 'HH:mm') : format(new Date(), 'HH:mm')}`,
        })
        fetchSummary().catch(() => {})
      } else {
        setResult({
          type: 'error',
          title: 'Check-In Failed',
          message: res.message || 'The server rejected your check-in request.',
        })
      }
    } catch (err: unknown) {
      setResult({
        type: 'error',
        title: 'Check-In Failed',
        message: getErrorMessage(err, 'Check-in failed. Please try again.'),
      })
    } finally {
      setIsCheckingIn(false)
    }
  }

  const handleCheckOut = async (image?: string, locationName?: string) => {
    if (!location) {
      setResult({
        type: 'error',
        title: 'GPS Required',
        message: locationError || 'GPS location is not available. Please enable location services and try again.',
      })
      return
    }
    setIsCheckingOut(true)
    setError(null)
    setResult(null)
    try {
      const res = await checkOut(location.lat, location.lng, locationName || undefined, image || undefined)
      if (res.success) {
        const coTime = res.check_out_time || new Date().toISOString()
        setCheckedOutTime(coTime)
        const parts = []
        if (res.check_out_time) parts.push(`Checked out at ${format(new Date(res.check_out_time), 'HH:mm')}`)
        else parts.push(`Checked out at ${format(new Date(), 'HH:mm')}`)
        if (res.working_hours) parts.push(`Working hours: ${res.working_hours.toFixed(2)} hrs`)
        setResult({
          type: 'success',
          title: 'Check-Out Successful',
          message: res.message || parts.join('. '),
        })
        fetchSummary().catch(() => {})
      } else {
        setResult({
          type: 'error',
          title: 'Check-Out Failed',
          message: res.message || 'The server rejected your check-out request.',
        })
      }
    } catch (err: unknown) {
      setResult({
        type: 'error',
        title: 'Check-Out Failed',
        message: getErrorMessage(err, 'Check-out failed. Please try again.'),
      })
    } finally {
      setIsCheckingOut(false)
    }
  }

  const hasCheckedIn = !!(summary?.check_in || checkedInTime)
  const hasCheckedOut = !!(summary?.check_out || checkedOutTime)
  const displayCheckIn = summary?.check_in || checkedInTime
  const displayCheckOut = summary?.check_out || checkedOutTime

  return {
    summary,
    summaryError,
    isLoading,
    needsAuth,
    isCheckingIn,
    isCheckingOut,
    hasCheckedIn,
    hasCheckedOut,
    displayCheckIn,
    displayCheckOut,
    location,
    locationLoading,
    locationError,
    result,
    setResult,
    error,
    setError,
    handleCheckIn,
    handleCheckOut,
    fetchSummary,
  }
}
